<template>
  <div
    class="w-full bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#1E293B] overflow-hidden transition-all duration-200 shadow-[0_1px_3px_rgba(0,0,0,0.03)]"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b border-slate-100 dark:border-[#1E293B] bg-slate-50/50 dark:bg-[#0E1526]/50"
    >
      <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1 pr-2">
        <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/70 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/40 flex items-center justify-center flex-shrink-0">
          <Megaphone class="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <h3 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white tracking-tight truncate">
              Faculty Announcements
            </h3>
            <span
              class="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60 shrink-0"
            >
              Faculty Broadcasts
            </span>
          </div>
          <p class="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate sm:whitespace-normal">
            Latest school announcements and direct faculty bulletins
          </p>
        </div>
      </div>

      <RouterLink
        to="/report"
        class="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors shrink-0 whitespace-nowrap ml-2"
      >
        <span>View All</span>
        <ArrowRight class="w-3.5 h-3.5" />
      </RouterLink>
    </div>

    <!-- Empty State -->
    <div
      v-if="!latestAnnouncements.length && !loading"
      class="flex flex-col items-center justify-center py-10 px-4 text-center"
    >
      <div
        class="h-11 w-11 rounded-2xl bg-blue-50 dark:bg-[#18243A] flex items-center justify-center mb-2.5"
      >
        <AlertCircle class="h-5 w-5 text-blue-600 dark:text-blue-400" />
      </div>

      <h4 class="text-sm font-semibold text-slate-800 dark:text-[#F1F5F9]">
        No Announcements
      </h4>

      <p class="text-xs text-slate-500 dark:text-[#7E8DA5] mt-0.5 max-w-sm">
        There are currently no active announcements directed to you or teaching staff.
      </p>
    </div>

    <!-- Loading Skeleton -->
    <div
      v-if="loading"
      class="p-3 sm:p-5 space-y-2.5 sm:space-y-3"
    >
      <div
        v-for="i in 3"
        :key="i"
        class="rounded-xl border border-slate-200/80 dark:border-[#263449] p-3 sm:p-4 flex gap-2.5 sm:gap-3 bg-white dark:bg-[#111C30]"
      >
        <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-xl skeleton-shimmer flex-shrink-0"></div>
        <div class="flex-1 space-y-2 py-0.5 min-w-0">
          <div class="h-4 skeleton-shimmer rounded-md w-3/4"></div>
          <div class="h-3 skeleton-shimmer rounded-md w-full"></div>
          <div class="h-2.5 skeleton-shimmer rounded-md w-1/3 pt-1"></div>
        </div>
      </div>
    </div>

    <!-- Announcement Cards List -->
    <div
      v-if="!loading && latestAnnouncements.length"
      class="p-3 sm:p-5 space-y-2.5 sm:space-y-3"
    >
      <div
        v-for="announcement in latestAnnouncements"
        :key="announcement._id"
        class="group rounded-2xl border transition-all duration-200 p-3 sm:p-4 relative overflow-hidden shadow-xs hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-[#111C30]"
        :class="[
          announcement.isPinned
            ? 'border-amber-300/80 dark:border-amber-700/60'
            : isDirectToCurrentUser(announcement)
            ? 'border-purple-300/80 dark:border-purple-700/60'
            : 'border-slate-200/90 dark:border-[#263449]',
        ]"
      >
        <!-- Top Status Accent Line -->
        <div
          class="absolute top-0 left-0 right-0 h-1"
          :class="[
            announcement.isPinned
              ? 'bg-gradient-to-r from-amber-400 to-amber-500'
              : isDirectToCurrentUser(announcement)
              ? 'bg-gradient-to-r from-purple-400 to-indigo-500'
              : 'bg-gradient-to-r from-blue-400 to-cyan-500',
          ]"
        ></div>
        <div class="flex gap-2.5 sm:gap-3 min-w-0">
          <!-- Type Icon -->
          <div
            class="flex-shrink-0 h-8 w-8 sm:h-9 sm:w-9 rounded-xl flex items-center justify-center mt-0.5"
            :class="[
              announcement.isPinned
                ? 'bg-blue-100 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400'
                : isDirectToCurrentUser(announcement)
                ? 'bg-purple-100 dark:bg-purple-950/80 text-purple-600 dark:text-purple-400'
                : 'bg-slate-200/70 dark:bg-[#172235] text-slate-700 dark:text-[#A6B4C8]',
            ]"
          >
            <Pin v-if="announcement.isPinned" class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <UserCheck v-else-if="announcement.targetType === 'specific_teacher'" class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <Users v-else class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </div>

          <!-- Content Body -->
          <div class="flex-1 min-w-0">
            <!-- Badges and Date -->
            <div class="flex flex-wrap items-center justify-between gap-1 sm:gap-1.5 mb-1.5">
              <div class="flex items-center gap-1 sm:gap-1.5 flex-wrap min-w-0">
                <!-- Pinned Chip -->
                <span
                  v-if="announcement.isPinned"
                  class="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide bg-blue-600 text-white shrink-0"
                >
                  PINNED
                </span>

                <!-- Target Audience Chip -->
                <span
                  v-if="isDirectToCurrentUser(announcement)"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 dark:border dark:border-purple-800/60 shrink-0"
                >
                  <UserCheck class="w-2.5 h-2.5 shrink-0" />
                  <span>Direct to You</span>
                </span>
                <span
                  v-else-if="announcement.targetType === 'specific_teacher'"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 dark:border dark:border-purple-800/60 shrink-0 max-w-full"
                >
                  <UserCheck class="w-2.5 h-2.5 shrink-0" />
                  <span class="truncate max-w-[130px] xs:max-w-[180px] sm:max-w-xs">Direct: {{ announcement.targetTeacher?.name || 'Teacher' }}</span>
                </span>
                <span
                  v-else
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 dark:border dark:border-blue-800/40 shrink-0"
                >
                  <Users class="w-2.5 h-2.5 shrink-0" />
                  <span>All Teachers</span>
                </span>

                <!-- Email Indicator Chip -->
                <span
                  v-if="announcement.emailSent"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border dark:border-emerald-800/40 shrink-0"
                  title="Sent via email to teacher inbox"
                >
                  <Mail class="w-2.5 h-2.5 shrink-0" />
                  <span>Emailed</span>
                </span>
              </div>

              <!-- Date -->
              <p class="text-[10px] sm:text-[11px] text-slate-400 dark:text-[#7E8DA5] whitespace-nowrap shrink-0 ml-auto sm:ml-0">
                {{ formatDate(announcement.createdAt) }}
              </p>
            </div>

            <!-- Title -->
            <h4 class="text-xs sm:text-sm md:text-base font-bold text-slate-900 dark:text-[#F1F5F9] leading-snug break-words line-clamp-2">
              {{ announcement.title }}
            </h4>

            <!-- Truncated Message -->
            <p class="text-xs text-slate-600 dark:text-[#A6B4C8] mt-1 leading-relaxed line-clamp-2 break-words">
              {{ announcement.message }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import {
    Megaphone,
    AlertCircle,
    Pin,
    Users,
    UserCheck,
    Mail,
    ArrowRight,
  } from "lucide-vue-next";

  const store = useStore();

  /* STORE STATE */
  const announcements = computed(() => store.state.announcements || []);
  const loading = computed(() => store.state.announcementsLoading);
  const currentUserId = computed(() => store.state.userId || store.getters.getUserId);

  /* Latest announcements */
  const latestAnnouncements = computed(() => {
    const now = new Date();

    return announcements.value
      .filter((a) => !a.expiryDate || new Date(a.expiryDate) > now)
      .slice()
      .sort((a, b) => {
        if (a?.isPinned && !b?.isPinned) return -1;
        if (!a?.isPinned && b?.isPinned) return 1;
        return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
      })
      .slice(0, 4);
  });

  function isDirectToCurrentUser(announcement) {
    if (announcement.targetType !== "specific_teacher") return false;
    const teacherId =
      announcement.targetTeacher?._id ||
      announcement.targetTeacher?.id ||
      announcement.targetTeacher;
    return teacherId && String(teacherId) === String(currentUserId.value);
  }

  /* Fetch announcements */
  onMounted(() => {
    if (!announcements.value.length) {
      store.dispatch("fetchAnnouncements");
    }
  });

  /* Date formatter */
  function formatDate(dateString) {
    if (!dateString) return "";

    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
</script>
